import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { 
  HelpCircle, 
  BookOpen, 
  Lightbulb, 
  Play, 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft,
  Trophy,
  Users,
  User,
  CalendarDays,
  BarChart2,
  MessageSquare,
  Settings,
  Home,
  Star,
  Target,
  Zap,
  Shield,
  Globe,
  Smartphone,
  Monitor
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface HelpStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
  action?: () => void;
}

interface HelpCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  steps: HelpStep[];
  tips: string[];
}

interface HelpSystemProps {
  onClose?: () => void;
}

const HelpSystem: React.FC<HelpSystemProps> = ({ onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('getting-started');
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [showTour, setShowTour] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(0);
  const location = useLocation();
  const { user } = useAuth();

  // Load completed steps from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('help-completed-steps');
    if (saved) {
      setCompletedSteps(JSON.parse(saved));
    }
  }, []);

  // Save completed steps to localStorage
  const markStepComplete = (stepId: string) => {
    const newCompleted = [...completedSteps, stepId];
    setCompletedSteps(newCompleted);
    localStorage.setItem('help-completed-steps', JSON.stringify(newCompleted));
  };

  const helpCategories: HelpCategory[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      description: 'Essential steps to begin using the cricket scoring system',
      icon: <Home className="h-5 w-5" />,
      steps: [
        {
          id: 'create-account',
          title: 'Create Your Account',
          description: 'Sign up and verify your email to access all features',
          icon: <User className="h-4 w-4" />,
          completed: completedSteps.includes('create-account'),
          action: () => {
            window.location.href = '/auth';
          }
        },
        {
          id: 'create-tournament',
          title: 'Create Your First Tournament',
          description: 'Set up a tournament to organize your cricket events',
          icon: <Trophy className="h-4 w-4" />,
          completed: completedSteps.includes('create-tournament'),
          action: () => {
            window.location.href = '/tournaments/new';
          }
        },
        {
          id: 'add-teams',
          title: 'Add Teams',
          description: 'Create teams that will participate in your tournament',
          icon: <Users className="h-4 w-4" />,
          completed: completedSteps.includes('add-teams'),
          action: () => {
            window.location.href = '/teams/new';
          }
        },
        {
          id: 'add-players',
          title: 'Add Players',
          description: 'Add players to your teams with their details',
          icon: <User className="h-4 w-4" />,
          completed: completedSteps.includes('add-players'),
          action: () => {
            window.location.href = '/players/new';
          }
        },
        {
          id: 'create-match',
          title: 'Create Your First Match',
          description: 'Schedule a match between teams',
          icon: <CalendarDays className="h-4 w-4" />,
          completed: completedSteps.includes('create-match'),
          action: () => {
            window.location.href = '/matches/new';
          }
        }
      ],
      tips: [
        'Start with creating a tournament - it\'s the foundation for everything else',
        'Use bulk import features to quickly add multiple teams or players',
        'Set realistic match schedules with adequate time between games',
        'Invite team captains to help manage their team rosters'
      ]
    },
    {
      id: 'scoring',
      title: 'Live Scoring',
      description: 'Learn how to score matches in real-time',
      icon: <Target className="h-5 w-5" />,
      steps: [
        {
          id: 'start-scoring',
          title: 'Start Match Scoring',
          description: 'Begin live scoring for a scheduled match',
          icon: <Play className="h-4 w-4" />,
          completed: completedSteps.includes('start-scoring')
        },
        {
          id: 'ball-by-ball',
          title: 'Ball-by-Ball Scoring',
          description: 'Record each ball with runs, wickets, and extras',
          icon: <Zap className="h-4 w-4" />,
          completed: completedSteps.includes('ball-by-ball')
        },
        {
          id: 'manage-overs',
          title: 'Manage Overs',
          description: 'Handle power plays, free hits, and extra balls',
          icon: <Shield className="h-4 w-4" />,
          completed: completedSteps.includes('manage-overs')
        },
        {
          id: 'player-stats',
          title: 'Track Player Statistics',
          description: 'Monitor individual player performance during the match',
          icon: <BarChart2 className="h-4 w-4" />,
          completed: completedSteps.includes('player-stats')
        }
      ],
      tips: [
        'Use keyboard shortcuts for faster scoring (1-6 for runs, W for wickets)',
        'Double-check extra balls and free hits before confirming',
        'Keep the scoreboard visible to players and spectators',
        'Pause scoring if there are interruptions or disputes'
      ]
    },
    {
      id: 'statistics',
      title: 'Statistics & Analytics',
      description: 'Understand and analyze match and player statistics',
      icon: <BarChart2 className="h-5 w-5" />,
      steps: [
        {
          id: 'view-stats',
          title: 'View Match Statistics',
          description: 'Analyze detailed statistics for completed matches',
          icon: <BarChart2 className="h-4 w-4" />,
          completed: completedSteps.includes('view-stats')
        },
        {
          id: 'player-performance',
          title: 'Player Performance',
          description: 'Track individual player statistics across matches',
          icon: <User className="h-4 w-4" />,
          completed: completedSteps.includes('player-performance')
        },
        {
          id: 'team-comparison',
          title: 'Team Comparisons',
          description: 'Compare team performance and rankings',
          icon: <Users className="h-4 w-4" />,
          completed: completedSteps.includes('team-comparison')
        },
        {
          id: 'tournament-standings',
          title: 'Tournament Standings',
          description: 'View overall tournament rankings and progress',
          icon: <Trophy className="h-4 w-4" />,
          completed: completedSteps.includes('tournament-standings')
        }
      ],
      tips: [
        'Statistics are automatically calculated as you score matches',
        'Use filters to analyze specific time periods or match types',
        'Export statistics for external analysis or reporting',
        'Compare performance across different tournaments'
      ]
    },
    {
      id: 'advanced-features',
      title: 'Advanced Features',
      description: 'Master advanced features and customization options',
      icon: <Settings className="h-5 w-5" />,
      steps: [
        {
          id: 'bulk-operations',
          title: 'Bulk Operations',
          description: 'Import multiple teams, players, or matches at once',
          icon: <Zap className="h-4 w-4" />,
          completed: completedSteps.includes('bulk-operations')
        },
        {
          id: 'custom-settings',
          title: 'Custom Settings',
          description: 'Configure system preferences and appearance',
          icon: <Settings className="h-4 w-4" />,
          completed: completedSteps.includes('custom-settings')
        },
        {
          id: 'feedback-system',
          title: 'Feedback & Reports',
          description: 'Submit feedback and generate custom reports',
          icon: <MessageSquare className="h-4 w-4" />,
          completed: completedSteps.includes('feedback-system')
        },
        {
          id: 'mobile-optimization',
          title: 'Mobile Optimization',
          description: 'Use the system effectively on mobile devices',
          icon: <Smartphone className="h-4 w-4" />,
          completed: completedSteps.includes('mobile-optimization')
        }
      ],
      tips: [
        'Use CSV templates for bulk imports to ensure data consistency',
        'Enable dark mode for better visibility in outdoor conditions',
        'Use the mobile app for on-field scoring convenience',
        'Regularly backup your data using export features'
      ]
    }
  ];

  const tourSteps = [
    {
      title: 'Welcome to Cricket Scoring System',
      description: 'This interactive tour will guide you through the main features of the system.',
      target: null
    },
    {
      title: 'Navigation',
      description: 'Use the top navigation to access different sections: Tournaments, Teams, Players, Matches, and Statistics.',
      target: 'nav'
    },
    {
      title: 'Dashboard Overview',
      description: 'The dashboard shows your key statistics and quick access to common actions.',
      target: 'dashboard'
    },
    {
      title: 'Quick Actions',
      description: 'Use the quick action buttons to create new tournaments, teams, players, or matches.',
      target: 'quick-actions'
    }
  ];

  const getProgressPercentage = (category: HelpCategory) => {
    const completed = category.steps.filter(step => step.completed).length;
    return (completed / category.steps.length) * 100;
  };

  const handleStepAction = (step: HelpStep) => {
    if (step.action) {
      step.action();
    }
    markStepComplete(step.id);
  };

  const nextTourStep = () => {
    if (currentTourStep < tourSteps.length - 1) {
      setCurrentTourStep(currentTourStep + 1);
    } else {
      setShowTour(false);
      setCurrentTourStep(0);
    }
  };

  const prevTourStep = () => {
    if (currentTourStep > 0) {
      setCurrentTourStep(currentTourStep - 1);
    }
  };

  return (
    <>
      <Dialog open={true} onOpenChange={(open) => { if (!open && onClose) onClose(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5" />
              Help & Tips Center
            </DialogTitle>
            <DialogDescription>
              Interactive guides and tips to help you master the cricket scoring system
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowTour(true)}
                className="gap-2"
              >
                <Play className="h-4 w-4" />
                Interactive Tour
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setActiveTab('getting-started')}
                className="gap-2"
              >
                <BookOpen className="h-4 w-4" />
                Quick Start Guide
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setActiveTab('scoring')}
                className="gap-2"
              >
                <Target className="h-4 w-4" />
                Scoring Guide
              </Button>
            </div>
            {/* Main Content */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                {helpCategories.map((category) => (
                  <TabsTrigger key={category.id} value={category.id} className="flex items-center gap-2">
                    {category.icon}
                    <span className="hidden sm:inline">{category.title}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
              {helpCategories.map((category) => (
                <TabsContent key={category.id} value={category.id} className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">{category.title}</h3>
                      <p className="text-sm text-muted-foreground">{category.description}</p>
                    </div>
                    <Badge variant="secondary">
                      {category.steps.filter(step => step.completed).length}/{category.steps.length} Complete
                    </Badge>
                  </div>
                  <Progress value={getProgressPercentage(category)} className="w-full" />
                  <div className="grid gap-4">
                    {category.steps.map((step) => (
                      <Card key={step.id} className={step.completed ? 'border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-900/50' : ''}>
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-full ${step.completed ? 'bg-green-100 text-green-600 dark:bg-green-800/30 dark:text-green-300' : 'bg-muted'}`}>
                                {step.completed ? <CheckCircle className="h-4 w-4" /> : step.icon}
                              </div>
                              <div>
                                <CardTitle className="text-base">{step.title}</CardTitle>
                                <CardDescription>{step.description}</CardDescription>
                              </div>
                            </div>
                            {step.action && !step.completed && (
                              <Button 
                                size="sm" 
                                onClick={() => handleStepAction(step)}
                                className="gap-2"
                              >
                                Start
                                <ArrowRight className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                  {/* Tips Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Lightbulb className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                        Pro Tips
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {category.tips.map((tip, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm">
                            <Star className="h-3 w-3 text-yellow-500 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>
      {showTour && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-background rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="text-center space-y-4">
              <div className="text-2xl font-bold">{tourSteps[currentTourStep].title}</div>
              <p className="text-muted-foreground">{tourSteps[currentTourStep].description}</p>
              <div className="flex justify-between items-center pt-4">
                <Button 
                  variant="outline" 
                  onClick={prevTourStep}
                  disabled={currentTourStep === 0}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>
                <div className="text-sm text-muted-foreground">
                  {currentTourStep + 1} of {tourSteps.length}
                </div>
                <Button onClick={nextTourStep}>
                  {currentTourStep === tourSteps.length - 1 ? 'Finish' : 'Next'}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpSystem; 
